import React, { useState } from 'react';
import { connect } from 'react-redux';

// Store
import { Dispatch } from '../../store/store';

// Components
import Modal from '../Modal/Modal';

const MIN_COUNT = 1;
const MAX_COUNT = 25;

type ComponentProps = {
  cycleId: string;
  cycleName: string;
  isVisible: boolean;
  onClose: () => void;
  onDuplicated?: () => void;
} & ReturnType<typeof mapDispatch>;

const DuplicateCycleModal: React.FC<ComponentProps> = ({
  cycleId,
  cycleName,
  isVisible,
  onClose,
  onDuplicated,
  duplicateCycle,
}) => {
  const [count, setCount] = useState(1);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = () => {
    if (isSaving) return;
    setCount(1);
    onClose();
  };

  const handleAccept = async () => {
    setIsSaving(true);
    try {
      await duplicateCycle({ id: cycleId, count });
      setCount(1);
      onClose();
      onDuplicated?.();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      modalTitle="Multi-duplicate cycle"
      isVisible={isVisible}
      onClose={handleClose}
      onAccept={handleAccept}
      acceptBtnText={isSaving ? 'Duplicating...' : 'Duplicate'}
      cancelBtnText="Cancel"
      acceptBtnDisabled={isSaving}
      cancelBtnDisabled={isSaving}
      showAcceptBtn
      showCancelBtn
    >
      <div className="flex flex-col gap-3 text-left">
        <p className="text-sm text-slate-gray">
          Creates exact copies of <span className="font-bold">{cycleName}</span> - same channels, meta data,
          recipe, and attachments - named <span className="font-bold">{cycleName}-01</span>,{' '}
          <span className="font-bold">-02</span>, etc.
        </p>
        <label className="flex flex-col gap-1 text-sm font-medium text-black dark:text-white">
          How many copies?
          <input
            type="number"
            min={MIN_COUNT}
            max={MAX_COUNT}
            value={count}
            disabled={isSaving}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (Number.isNaN(value)) return;
              setCount(Math.min(MAX_COUNT, Math.max(MIN_COUNT, value)));
            }}
            className="w-full rounded border border-stroke bg-transparent px-3 py-2 outline-none focus:border-primary dark:border-strokedark dark:bg-boxdark"
          />
        </label>
      </div>
    </Modal>
  );
};

const mapDispatch = (dispatch: Dispatch) => ({
  duplicateCycle: dispatch.cycles.duplicateCycle,
});

export default connect(null, mapDispatch)(DuplicateCycleModal);
